import React, { useContext, useEffect, useState } from "react";
import StepOne from "./CohortDetails";
import StepTwo from "./PricingAndSchedule";
import StepThree from "./OtherDetails";
import NavBar from "../../NavBar";
import { AppContext } from "../../../context";
import { Link, useNavigate } from "react-router-dom";
import {
  GetCohortDetails,
  SaveCohortAsDraft,
  UpdateCohortAsDraft,
} from "../../../services/cohortService";
import LoadingView from "../../LoadingView";
import { notification, Popconfirm } from "antd";
import QalortButton from "../../Button";
import SuccessCohort from "./SuccessCohort";
import dayjs from "dayjs";
import { useCategories } from "../../../hooks/useCategories";

function CreateCohort() {
  const [activeStep, setActiveStep] = useState(1);
  const { formFields, updateContext } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [highestLevel, setHighestLevel] = useState(1);
  const [successModal, setSuccessModal] = useState(false);
  const [newCohort, setNewCohort] = useState(null);
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const categories = useCategories();

  const handleProceed = () => {
    if (highestLevel < activeStep + 1) {
      setHighestLevel(activeStep + 1);
    }
    if (activeStep < 3) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const fetchCohortInfo = async () => {
    setLoading(true);
    try {
      const res = await GetCohortDetails(
        formFields?.slug || params.get("cohort_id")
      );
      const responseData = res.data.data;
      const cohortInfo = {
        ...formFields,
        ...responseData,
        mentees: responseData.capacity,
        topics: responseData?.topics?.map((t) => t.title),
        requirements: responseData?.requirements?.map((t) => t.content),
        audiences: responseData?.audiences?.map((t) => t.audience),
        price: responseData?.price ? responseData.price / 100 : 0,
      };
      if (!cohortInfo.category_id) {
        cohortInfo.category_id = categories.find(
          (c) => c.category === responseData?.category
        )?._id;
      }
      updateContext(cohortInfo);
    } catch (error) {
      notification.error({
        message: error.response?.data?.message || "An error occured",
      });
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const hopToLevel = (level) => {
    if (highestLevel < level) {
      return;
    }
    setActiveStep(level);
  };

  const saveDraft = async () => {
    const refinedStartDate =
      typeof formFields.start_date === "string"
        ? formFields.start_date.split("T")[0]
        : dayjs(formFields.start_date).format("YYYY-MM-DD");
    const formData = {
      ...formFields,
      start_date: refinedStartDate,
      end_date: !formFields.end_date
        ? refinedStartDate
        : formFields.end_date && typeof formFields.end_date === "string"
        ? formFields.end_date.split("T")[0]
        : dayjs(formFields.end_date).format("YYYY-MM-DD"),
    };

    const data = {
      banner: formData.banner,
      title: formData.title,
      description: formData.description,
      end_date:
        formData.end_date?.split("T")[0] || formData.start_date?.split("T")[0],
      start_date: formData.start_date,
      schedules: formData.schedules,
      frequency: formData.frequency || "once",
      capacity: Number(formData.capacity),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      status: "active",
      currency: formData.currency,
      faqs: formData.faqs,
      price: formData.price,
      auto_record: false,
      intro_video: formData.intro_video,
      long_description: formData.long_description,
      category_id: formData.category_id,
      capacity: Number(formData.mentees || formData.capacity),
      topics: formData?.topics?.map((t) => {
        return {
          title: t,
          topic: t,
        };
      }),
      audiences: formData?.audiences?.map((t) => {
        return {
          audience: t,
        };
      }),
      requirements: formData?.requirements?.map((t) => {
        return {
          content: t,
        };
      }),
    };

    setSavingDraft(true);
    try {
      formFields.id
        ? await UpdateCohortAsDraft(data, formFields.id)
        : await SaveCohortAsDraft(data);
      notification.success({
        message: "Draft saved successfully",
      });
      navigate("/cohort");
    } catch (error) {
      notification.error({
        message: error?.response?.data?.message || "An error occured",
      });
    } finally {
      setSavingDraft(false);
    }
  };

  useEffect(() => {
    if (formFields?.slug || params.get("cohort_id")) {
      fetchCohortInfo();
      setHighestLevel(3);
    }

    return () => {
      updateContext({});
    };
  }, [categories]);

  return (
    <div>
      <NavBar
        title={formFields.title || "Create New Cohort"}
        className={`lg:sticky block lg:block ${!!newCohort ? "bg-white" : ""}`}
        description={
          formFields.description || "Your Cohort description will show up here"
        }
      />
      <NavBar
        title={formFields.title || "Cohorts"}
        className={`lg:sticky block lg:hidden ${!!newCohort ? "bg-white" : ""}`}
        description={
          formFields.description || (
            <p>
              Your Cohort description will show up here{" "}
              <Link to="/help-center">
                <span className="pri"> Check our help Centre.</span>
              </Link>
            </p>
          )
        }
      />
      {!!newCohort ? (
        <SuccessCohort
          successOpen={!!newCohort}
          setSuccessModal={setSuccessModal}
          newCohort={newCohort}
        />
      ) : (
        <LoadingView loading={loading}>
          <div className="pt-[66px] mb-5 border-b border-light-color block lg:hidden">
            <div className=" py-4 px-5">
              <h6 className="mb-0 text-dark font-semibold">
                {formFields.title || "Create New Cohort"}
              </h6>
              <p className="p-xs mb-0 text-tint font-medium">
                {formFields.description ||
                  "Your Cohort description will show up here"}
              </p>
            </div>
          </div>
          <div className="lg:pt-5 pb-[150px] [@media(max-width:1200px)]:px-5 px-0 flex flex-col gap-4 relative">
            <StepOne
              isOpen={activeStep === 1}
              isDone={activeStep > 1}
              handleProceed={handleProceed}
              formFields={formFields}
              updateContext={updateContext}
              hopToLevel={hopToLevel}
            />
            <StepTwo
              isOpen={activeStep === 2}
              isDone={activeStep > 2}
              handleProceed={handleProceed}
              handleBack={handleBack}
              formFields={formFields}
              updateContext={updateContext}
              hopToLevel={hopToLevel}
            />
            <StepThree
              isOpen={activeStep === 3}
              isDone={activeStep > 3}
              handleProceed={handleProceed}
              handleBack={handleBack}
              formFields={formFields}
              updateContext={updateContext}
              hopToLevel={hopToLevel}
              setNewCohort={setNewCohort}
              // setSuccessModal={setSuccessModal}
            />
            <div className="fixed bottom-0 right-0 bg-white p-5 border-grey-2 border-t w-full [@media(min-width:1200px)]:w-[calc(100%-230px)]">
              <div className="max-w-[867px] mx-auto flex justify-between">
                <Popconfirm
                  onConfirm={() => {
                    {
                      updateContext({});
                      setActiveStep(1);
                      navigate(-1);
                    }
                  }}
                  title="Are you sure you want to discard this cohort?"
                >
                  <button className="border border-[#FB3E6C] text-[#FB3E6C] font-semibold text-sm py-2 px-3 flex items-center rounded-md justify-center">
                    Discard
                  </button>
                </Popconfirm>
                {!(formFields?.slug || params.get("cohort_id")) && (
                  <QalortButton
                    loading={savingDraft}
                    onClick={saveDraft}
                    className="bg-pri-tint border border-pri font-semibold pri text-sm py-3 px-6 flex items-center rounded-md justify-center"
                  >
                    Save For Later
                  </QalortButton>
                )}
              </div>
            </div>
          </div>
        </LoadingView>
      )}
    </div>
  );
}

export default CreateCohort;
